import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { CarCardData } from '@/components/cars/CarCard';

interface CompareContextValue {
  selectedCars: CarCardData[];
  addCar: (car: CarCardData) => void;
  removeCar: (variantId: string) => void;
  swapCar: (currentVariantId: string, newCar: CarCardData) => void;
  isSelected: (variantId: string) => boolean;
  clearAll: () => void;
  maxCars: number;
}

const MAX_COMPARE_CARS = 5;
const STORAGE_KEY = 'bilbase-compare-cars';

function loadFromStorage(): CarCardData[] {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

function saveToStorage(cars: CarCardData[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cars));
  } catch { /* ignore */ }
}

const CompareContext = createContext<CompareContextValue | undefined>(undefined);

export function CompareProvider({ children }: { children: ReactNode }) {
  const [selectedCars, setSelectedCars] = useState<CarCardData[]>(loadFromStorage);

  useEffect(() => {
    saveToStorage(selectedCars);
  }, [selectedCars]);

  const addCar = useCallback((car: CarCardData) => {
    setSelectedCars(prev => {
      if (prev.length >= MAX_COMPARE_CARS) return prev;
      if (prev.some(c => c.variant.id === car.variant.id)) return prev;
      return [...prev, car];
    });
  }, []);

  const removeCar = useCallback((variantId: string) => {
    setSelectedCars(prev => prev.filter(c => c.variant.id !== variantId));
  }, []);

  const swapCar = useCallback((currentVariantId: string, newCar: CarCardData) => {
    setSelectedCars(prev => {
      const idx = prev.findIndex(c => c.variant.id === currentVariantId);
      if (idx === -1) return prev;
      // Hvis ny variant allerede er i listen (anden plads), behold som det er
      if (prev.some((c, i) => i !== idx && c.variant.id === newCar.variant.id)) return prev;
      const next = [...prev];
      next[idx] = newCar;
      return next;
    });
  }, []);

  const isSelected = useCallback((variantId: string) => {
    return selectedCars.some(c => c.variant.id === variantId);
  }, [selectedCars]);

  const clearAll = useCallback(() => {
    setSelectedCars([]);
  }, []);

  return (
    <CompareContext.Provider value={{
      selectedCars,
      addCar,
      removeCar,
      swapCar,
      isSelected,
      clearAll,
      maxCars: MAX_COMPARE_CARS,
    }}>
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
}
